/**
 * /mail/settings — overage_mode + monthly spend cap (OCP-162 + OCP-128).
 *
 * Wraps the existing `/v1/portal/me/mail-overage-settings` backend
 * (shipped under OCP-162) in a dedicated discoverable tab. Previously
 * the only surface was a nested section on the Mail subscription
 * detail page — customers don't naturally find it there.
 *
 * Two controls:
 *   - overage_mode toggle: hard_gate | use_credits
 *   - monthly cap (in EUR, displayed as cents under the hood)
 *
 * Plus read-only state widgets: current credit balance, current-
 * month accrued spend, "would_block" indicator if next send would
 * fail.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MailComplaintNoticeToggle } from '../components/MailComplaintNoticeToggle';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { fmtMoney, fmtNumber } from '../lib/format-money';
import { ROLE_LABEL, useMailWriteAccess } from '../lib/use-mail-write-access';

interface Settings {
  overage_mode: 'hard_gate' | 'use_credits';
  monthly_cap_eur_cents: number;
  current_month_spend_eur_cents: number;
  credit_balance: number;
  would_block: boolean;
  /** False when customer only has free Mail sub (or no Mail sub at
   * all) — use_credits mode is gated behind a paid plan (OCP-173). */
  can_use_credits: boolean;
  /** OCP-177 — live subscription monthly usage from OMS. null when
   * OMS unconfigured or customer not yet in OMS. */
  subscription_used: number | null;
  subscription_total: number | null;
}

export function MailSettingsPage() {
  const { t } = useTranslation('mail-settings');
  const [data, setData] = useState<Settings | null>(null);
  const [draftMode, setDraftMode] = useState<Settings['overage_mode']>('hard_gate');
  const [draftCapEur, setDraftCapEur] = useState('0');
  const [loadState, setLoadState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // OCP-538 - only the 'saved' flash auto-reverts; 'saving'/'error' must
  // hold, so this is an effect-scoped timer (lifecycle-bound), not
  // useTransientState which would revert every value.
  useEffect(() => {
    if (saveState !== 'saved') return;
    const t = setTimeout(() => setSaveState('idle'), 2000);
    return () => clearTimeout(t);
  }, [saveState]);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  // OCP-545 - spend controls are the one Mail mutation a billing-only
  // member SHOULD be able to make; a viewer still cannot.
  const { canChangeSpend, role } = useMailWriteAccess();

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoadState('loading');
    try {
      const r = await api<Settings>('/v1/portal/me/mail-overage-settings', { signal });
      setData(r);
      setDraftMode(r.overage_mode);
      setDraftCapEur((r.monthly_cap_eur_cents / 100).toString());
      setLoadState('idle');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError) {
        setErrorDetail(t('error.loadHttp', { status: err.status }));
      } else {
        setErrorDetail(t('error.load'));
      }
      setLoadState('error');
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  async function save() {
    setSaveState('saving');
    setErrorDetail(null);
    const capEur = parseFloat(draftCapEur);
    if (Number.isNaN(capEur) || capEur < 0) {
      setSaveState('error');
      setErrorDetail(t('error.capInvalid'));
      return;
    }
    try {
      const updated = await api<Settings>('/v1/portal/me/mail-overage-settings', {
        method: 'PATCH',
        body: {
          overage_mode: draftMode,
          monthly_cap_eur_cents: Math.round(capEur * 100),
        },
      });
      setData(updated);
      setSaveState('saved');
      // Clear the "saved" flash after a moment.
    } catch (err) {
      setSaveState('error');
      if (err instanceof ApiError && err.status === 403) {
        const body = err.body as { error?: string } | null;
        // OCP-173 — overage_mode='use_credits' on Free plan.
        if (body?.error === 'requires_paid_subscription') {
          setErrorDetail(t('error.requiresPaid'));
          setDraftMode(data?.overage_mode ?? 'hard_gate');
          return;
        }
        // OCP-251 — requireOmsSub guard rejected the save because the
        // customer has no active orboto Mail subscription at all.
        if (body?.error === 'feature_requires_oms_subscription') {
          setErrorDetail(t('error.requiresOms'));
          setDraftMode(data?.overage_mode ?? 'hard_gate');
          return;
        }
      }
      if (err instanceof ApiError) {
        setErrorDetail(t('error.saveHttp', { status: err.status }));
      } else {
        setErrorDetail(t('error.save'));
      }
    }
  }

  if (loadState === 'loading') {
    return <p className="text-sm text-gray-500">{t('loading')}</p>;
  }
  if (loadState === 'error' || !data) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-800 dark:text-red-300"
      >
        {errorDetail ?? t('error.load')}
      </div>
    );
  }

  const dirty =
    draftMode !== data.overage_mode ||
    Math.round(parseFloat(draftCapEur) * 100) !== data.monthly_cap_eur_cents;

  return (
    <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('overage.title')}</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('overage.lead')}</p>

        <fieldset className="mt-4 space-y-3">
          <legend className="sr-only">{t('overage.legend')}</legend>
          <ModeOption
            value="hard_gate"
            current={draftMode}
            onChange={setDraftMode}
            title={t('overage.hardGate.title')}
            description={t('overage.hardGate.description')}
          />
          <ModeOption
            value="use_credits"
            current={draftMode}
            onChange={setDraftMode}
            disabled={!data.can_use_credits}
            title={t('overage.useCredits.title')}
            description={data.can_use_credits ? t('overage.useCredits.enabled') : t('overage.useCredits.disabled')}
          />
          {!data.can_use_credits && (
            <Link
              to="/products"
              className="-mt-2 ml-9 block text-xs text-brand-500 hover:underline"
            >
              {t('overage.upgrade')}
            </Link>
          )}
        </fieldset>

        <label className="mt-5 block">
          <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('cap.title')}
          </span>
          <span className="block text-xs text-gray-600 dark:text-gray-400">{t('cap.help')}</span>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-500">€</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={draftCapEur}
              onChange={(e) => setDraftCapEur(e.target.value)}
              className="w-32 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm tabular-nums"
            />
            <span className="text-sm text-gray-500">{t('cap.perMonth')}</span>
          </div>
        </label>

        {!canChangeSpend && role && (
          <p
            role="status"
            data-testid="mail-spend-read-only"
            className="mt-4 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
          >
            {t('readOnly', { role: ROLE_LABEL[role] })}
          </p>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saveState === 'saving' || !canChangeSpend}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {saveState === 'saving' ? t('saving') : t('save')}
          </button>
          {saveState === 'saved' && (
            <span className="text-xs text-emerald-700 dark:text-emerald-300">{t('saved')}</span>
          )}
          {saveState === 'error' && errorDetail && (
            <span role="alert" className="text-xs text-red-700 dark:text-red-300">
              {errorDetail}
            </span>
          )}
        </div>
      </section>

      <aside className="space-y-4">
        <SubscriptionUsageCard
          used={data.subscription_used}
          total={data.subscription_total}
        />
        <StateCard
          title={t('cards.creditBalance')}
          value={fmtNumber(data.credit_balance)}
          subtitle={t('cards.sendsRemaining')}
          cta={
            <Link to="/mail/connections" className="text-xs text-brand-500 hover:underline">
              {t('cards.topUp')}
            </Link>
          }
        />
        <StateCard
          title={t('cards.monthSpend')}
          value={fmtMoney(data.current_month_spend_eur_cents)}
          subtitle={t('cards.ofCap', { cap: fmtMoney(data.monthly_cap_eur_cents) })}
        />
        {/* OCP-621 - the once-per-month spam-complaint notice switch. */}
        <MailComplaintNoticeToggle />
        {data.would_block && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-4 text-xs text-amber-900 dark:text-amber-200">
            <strong>{t('wouldBlock.lead')}</strong>{' '}
            {data.overage_mode === 'hard_gate'
              ? t('wouldBlock.hardGate')
              : data.credit_balance <= 0
                ? t('wouldBlock.noCredits')
                : t('wouldBlock.capReached')}
          </div>
        )}
      </aside>
    </div>
  );
}

function ModeOption({
  value,
  current,
  onChange,
  title,
  description,
  disabled,
}: {
  value: Settings['overage_mode'];
  current: Settings['overage_mode'];
  onChange: (v: Settings['overage_mode']) => void;
  title: string;
  description: string;
  disabled?: boolean;
}) {
  const selected = value === current;
  return (
    <label
      className={`flex items-start gap-3 rounded-lg border p-3 transition ${
        disabled
          ? 'cursor-not-allowed border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 opacity-70'
          : 'cursor-pointer ' +
            (selected
              ? 'border-brand-400 bg-brand-50/40'
              : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700')
      }`}
    >
      <input
        type="radio"
        name="overage_mode"
        value={value}
        checked={selected}
        disabled={disabled}
        onChange={() => onChange(value)}
        className="mt-1"
      />
      <span>
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
        <span className="block text-xs text-gray-600 dark:text-gray-400">{description}</span>
      </span>
    </label>
  );
}

function SubscriptionUsageCard({
  used,
  total,
}: {
  used: number | null;
  total: number | null;
}) {
  const { t } = useTranslation('mail-settings');
  if (used === null || total === null) {
    return (
      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {t('cards.usageTitle')}
        </h3>
        <div className="mt-1 text-2xl font-semibold text-gray-400">—</div>
        <div className="text-xs text-gray-500">{t('cards.usageEmpty')}</div>
      </section>
    );
  }
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const tone =
    pct >= 95 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-brand-500';
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {t('cards.thisMonth')}
      </h3>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
          {fmtNumber(used)}
        </span>
        <span className="text-sm text-gray-500">{t('cards.ofSends', { total: fmtNumber(total) })}</span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={used}
        className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800"
      >
        <div className={`h-full transition-all ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </section>
  );
}

function StateCard({
  title,
  value,
  subtitle,
  cta,
}: {
  title: string;
  value: string;
  subtitle?: string;
  cta?: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {title}
        </h3>
        {cta}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
        {value}
      </div>
      {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
    </section>
  );
}
